import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media, { MediaElement } from '@/components/base/media'
import Motion from '@/components/base/motion'
import ScrollIcon from '@/components/base/scroll-icon'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { heroRevealAnimation } from './animations'
import { cn } from '@/lib/utils'

export type HeroProps = HTMLAttributes<HTMLElement> &
  Partial<{
    overline: string
    title: string
    media: MediaElement
  }>

const Hero = ({ title, overline, media, ...props }: HeroProps) => {
  return (
    <Motion animation={heroRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <div
            className={cn(
              'absolute left-0 top-0 h-full w-full overflow-hidden bg-dark',
              'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/30'
            )}
          >
            <div
              className="absolute left-0 top-0 h-full w-full"
              data-motion-media
            >
              <Media fill fit="cover" {...media} />
            </div>
          </div>
          <Container className="min-h-svh-vh relative flex items-center text-light">
            <Grid>
              <div
                className={cn(
                  'col-span-4 text-center md:col-span-6 lg:col-span-8 lg:col-start-3',
                  'grid gap-y-[1.6rem] md:gap-y-[3.2rem]'
                )}
              >
                {overline ? (
                  <Typography
                    variant="h6"
                    component="h2"
                    className="font-timesnow"
                    data-motion-overline
                  >
                    {overline}
                  </Typography>
                ) : null}

                {title ? (
                  <Typography
                    variant="h1"
                    component="h1"
                    className="font-gillsans font-light uppercase"
                    data-motion-title
                  >
                    {title}
                  </Typography>
                ) : null}
              </div>
            </Grid>
          </Container>

          <ScrollIcon className="absolute bottom-[6rem] left-1/2 -translate-x-1/2 md:bottom-[4rem] lg:bottom-[5rem] xl:bottom-[6rem]" />

          <div
            className="pointer-events-none fixed left-0 top-0 z-10 h-screen w-full bg-dark"
            data-motion-mask
          />
        </Section>
      )}
    </Motion>
  )
}

export default Hero
